import PropTypes from 'prop-types';
import React, { useState } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedDate } from 'react-intl';
import { Link } from 'react-router-dom';

import { GhostButton, IconButton } from '@bibliocommons/base-buttons';
import { Dropdown } from '@bibliocommons/base-dropdown';
import { Delete as IconDelete, Edit as IconEdit, Plus as IconPlus, Spinner } from '@bibliocommons/base-icons';
import { Link as BaseLink } from '@bibliocommons/base-links';
import Heading from '@bibliocommons/heading';
import { Modal, ModalContent, ModalFooter, ModalHeader } from '@bibliocommons/modal';
import { Table, TableHeader, TableHeading, TableBody, TableRow, TableCell } from '@bibliocommons/table';

import { LOCALE_CODE } from 'app/constants/language';
import { browseHomePageSummaryShape } from 'app/shapes/admin/browse/home';
import { makeHomeCreatePageRoute, makeHomeEditPageRoute } from 'app/helpers/admin/browse/home';

import './AdminBrowseHomeListPage.scss';

const AdminBrowseHomeListPage = ({
  activeBrowseHomePageId,
  browseHomePages,
  isFetching,
  isSaving,
  localeCode,
  localeDropdownOptions,
  mapLocaleCodeToLabel,
  onActivate,
  onDelete,
  onLocaleCodeChange
}) => {
  const [browseHomePageIdToDelete, setBrowseHomePageIdToDelete] = useState(null);

  const openConfirmDeleteModal = browseHomePageId => {
    setBrowseHomePageIdToDelete(browseHomePageId);
  };

  const closeConfirmDeleteModal = () => {
    setBrowseHomePageIdToDelete(null);
  };

  const handleDeleteClick = () => {
    onDelete(browseHomePageIdToDelete);
    closeConfirmDeleteModal();
  };

  const isConfirmationModalOpen = Boolean(browseHomePageIdToDelete);
  const nameOfPageToDelete = isConfirmationModalOpen
    ? browseHomePages.find(browseHomePage => {
        return browseHomePage.browseHomePageId === browseHomePageIdToDelete;
      })?.name ?? ''
    : '';

  const createPageRoute = makeHomeCreatePageRoute(localeCode);
  const hasNoResults = !isFetching && browseHomePages.length === 0;

  return (
    <div className="cp-admin-browse-home-list-page container">
      <div className="heading-wrapper">
        <Heading className="heading" size="large" tagName="h1" variant>
          Browse home page
        </Heading>
        <div className="locale-selector">
          <span className="locale-label">Language:</span>
          <Dropdown
            dataKey="DropdownLocaleCode"
            disabled={isSaving}
            handleSelect={onLocaleCodeChange}
            options={localeDropdownOptions}
          />
        </div>
      </div>

      <Modal handleClose={closeConfirmDeleteModal} key="BrowseAdminHome_ConfirmDelete" open={isConfirmationModalOpen}>
        <ModalHeader title="Delete page" />
        <ModalContent>
          <p>Are you sure you want to delete the page &apos;{nameOfPageToDelete}&apos;?</p>
        </ModalContent>
        <ModalFooter handlePrimaryAction={handleDeleteClick} primaryActionButtonText="Delete page" />
      </Modal>

      <Table>
        <TableHeader>
          <TableHeading>Name</TableHeading>
          <TableHeading>Creator</TableHeading>
          <TableHeading>Last updated</TableHeading>
          <TableHeading>Status</TableHeading>
          <TableHeading />
        </TableHeader>
        {!isFetching && (
          <TableBody>
            {browseHomePages.map(browseHomePage => {
              const { browseHomePageId, createdBy, name, updatedAt, updatedBy } = browseHomePage;

              const isActive = activeBrowseHomePageId === browseHomePageId;

              const editPageRoute = makeHomeEditPageRoute(localeCode, browseHomePageId);

              return (
                <TableRow key={browseHomePageId}>
                  <TableCell className="home-page-name">{name}</TableCell>
                  <TableCell>{createdBy}</TableCell>
                  <TableCell>
                    <FormattedDate day="2-digit" month="long" value={updatedAt} year="numeric" /> by {updatedBy}
                  </TableCell>
                  <TableCell>
                    <span className="actions">
                      <span>{isActive ? 'Active' : 'Draft'}</span>
                      {!isActive && (
                        <GhostButton
                          className="action"
                          dataKey="action-activate"
                          handleClick={() => onActivate(browseHomePageId)}
                          isLoading={isSaving}
                        >
                          Activate
                        </GhostButton>
                      )}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="actions">
                      <Link className="action action--link-icon" to={editPageRoute}>
                        <IconEdit />
                      </Link>
                      <IconButton
                        className="action"
                        dataKey="action-delete"
                        handleClick={() => openConfirmDeleteModal(browseHomePageId)}
                        isLoading={isSaving}
                      >
                        <IconDelete />
                      </IconButton>
                    </span>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        )}
      </Table>
      {isFetching && (
        <div className="fetching-wrapper">
          <Spinner />
        </div>
      )}

      {hasNoResults && (
        <div className="no-results">
          <Heading size="medium" tagName="h2">
            No {mapLocaleCodeToLabel(localeCode)} home pages created yet.
          </Heading>
          <p>Click on the create new home page button below to begin creating a home page.</p>
        </div>
      )}

      <BaseLink className="create-new" dataKey="link-create-new" href={createPageRoute}>
        <IconPlus className="icon-plus" />
        Create new home page
      </BaseLink>
    </div>
  );
};

AdminBrowseHomeListPage.propTypes = {
  activeBrowseHomePageId: PropTypes.string,
  browseHomePages: PropTypes.arrayOf(browseHomePageSummaryShape).isRequired,
  isFetching: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
  localeCode: PropTypes.oneOf(Object.values(LOCALE_CODE)).isRequired,
  localeDropdownOptions: ImmutablePropTypes.listOf(
    ImmutablePropTypes.mapContains({
      disabled: PropTypes.bool.isRequired,
      label: PropTypes.string.isRequired,
      selected: PropTypes.bool.isRequired,
      value: PropTypes.oneOf(Object.values(LOCALE_CODE)).isRequired
    })
  ).isRequired,
  mapLocaleCodeToLabel: PropTypes.func.isRequired,
  onActivate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onLocaleCodeChange: PropTypes.func.isRequired
};

export default AdminBrowseHomeListPage;
